import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, ExternalLink } from 'lucide-react';
import intelligence from '../assets/Intelligence.gif';
import Loading from '../Components/Loading';

export default function ProductCategoryAndComplianceRequirements({
  exportingCountry,
  destinationCountry,
  businessDetails,
  products,
  selectedState,
  selectedDistrict,
  keywords
}) {
  const [response, setResponse] = useState(null);
  const [tabledata, setTabledata] = useState(null);
  const [summary, setSummary] = useState('');
  const [loadingResponse, setLoadingResponse] = useState(true);
  const [loadingTabledata, setLoadingTabledata] = useState(true);
  const [loadingText, setLoadingText] = useState('Loading.');

  useEffect(() => {
    const fetchData = async () => {
      setLoadingResponse(true);
      setLoadingTabledata(true);
      try {
        const productsArray = Array.isArray(products) ? products : [];
        const keywordsArray = Array.isArray(keywords) ? keywords : [];
        const query = `${productsArray.join(' ')} ${keywordsArray.join(' ')}`;
        const requestBody = { query: `I want to sell ${query}` };

        const responseResult = await axios.post('https://cross-intelligence-50023657941.development.catalystappsail.in/amazon-product-category', requestBody);
        setResponse(responseResult.data);
        setLoadingResponse(false);

        const summaryRequestBody = {
          prompt: `Think like you are my International Business Advisor you are helping me to identify Product Category and Compliance Requirements for my products ${query}. so based on bellow content write a short paragraph as like my you are Smart International Business Advisors dont mention USA ${responseResult.data.documents.map(doc => doc.extractedText).join(' ')}`
        };
        
        const summaryResult = await axios.post('https://cross-intelligence-50023657941.development.catalystappsail.in/genarate-summary', summaryRequestBody);
        setSummary(summaryResult.data.answer);

        const extractedTextArray = responseResult.data.documents.map(doc => doc.extractedText).join(' ');
        const tabledataRequestBody = {
          query: `I want to sell ${query}`,
          documents: extractedTextArray
        };
        const tabledataResult = await axios.post('https://cross-intelligence-50023657941.development.catalystappsail.in/amazon-seller-central', tabledataRequestBody);
        setTabledata(tabledataResult.data);
        setLoadingTabledata(false);

        sessionStorage.setItem('amazonSellerCentral', JSON.stringify(tabledataResult.data));
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoadingResponse(false);
        setLoadingTabledata(false);
      }
    };

    const storedResponse = sessionStorage.getItem('ProductCategoryAndComplianceRequirement');
    const storedTabledata = sessionStorage.getItem('amazonSellerCentral');

    if (storedResponse && storedTabledata) {
      setResponse(JSON.parse(storedResponse));
      setTabledata(JSON.parse(storedTabledata));
      setLoadingResponse(false);
      setLoadingTabledata(false);
    } else {
      fetchData();
    }
  }, [products, keywords]);

  useEffect(() => {
    if (response) {
      sessionStorage.setItem('ProductCategoryAndComplianceRequirement', JSON.stringify(response));
    }
  }, [response]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingText(prev => {
        if (prev === 'Loading.') return 'Loading..';
        if (prev === 'Loading..') return 'Loading...';
        return 'Loading.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  if (loadingResponse) {
    return (
      <Loading />
    );
  }

  if (!response) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500">
        <AlertCircle className="w-6 h-6 mr-2" />
        <span>Error loading data</span>
      </div>
    );
  }

  const referenceUrls = [
    ...response.documents.map(doc => doc.downloadURL),
    ...(tabledata ? tabledata["Reference URLs"] : [])
  ];

  return (
    <div className="container mx-auto p-4 space-y-8">
      <h1 className="text-3xl text-center font-medium">Product Category and Compliance Requirements</h1>

      {summary && (
        <div className="bg-gray-100 p-4 rounded-md shadow-md mb-4">
          <p className="text-lg font-medium">
            {summary.split(/(\*\*.*?\*\*)/).map((part, index) =>
              part.startsWith("**") && part.endsWith("**") ? (
                <span key={index}>
                  <strong>{part.slice(2, -2)}</strong>
                  <a
                    href="#reference-urls"
                    className="text-blue-500 ml-1"
                    aria-label={`Reference ${index + 1}`}
                  >
                    [{index + 1}]
                  </a>
                </span>
              ) : (
                part
              )
            )}
          </p>
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <h2 className="text-xl font-semibold bg-gray-100 p-4">Product Categories</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtype</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documentation</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {response.documents.map((doc) => (
                <tr key={doc._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{doc.category}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.subtype}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <a href={doc.downloadURL} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline flex items-center">
                      Link
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {loadingTabledata ? (
        <Loading />
      ) : (
        tabledata && (
          <div className="grid md:grid-cols-2 gap-8">
            <RequirementTable title="Mandatory Requirements" requirements={tabledata["Mandatory Requirements"]} />
            <RequirementTable title="Recommended Requirements" requirements={tabledata["Recommended Requirements"]} />
          </div>
        )
      )}

      <div className="bg-white shadow-md rounded-lg overflow-hidden" id="reference-urls">
        <h2 className="text-xl font-semibold bg-gray-100 p-4">Reference URLs</h2>
        <ul className="divide-y divide-gray-200">
          {referenceUrls.map((url, index) => (
            <li key={index} className="p-4 hover:bg-gray-50">
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline flex items-center">
                {url}
                <ExternalLink className="w-4 h-4 ml-2 flex-shrink-0" />
              </a>
              {tabledata && tabledata["Reference URLs"][index] && tabledata["Reference URLs"][index].downloadURL && (
                <a href={tabledata["Reference URLs"][index].downloadURL} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline flex items-center ml-2">
                  Download
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function RequirementTable({ title, requirements }) {
  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <h2 className="text-xl font-semibold bg-gray-100 p-4">{title}</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requirement</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requirements.map((req, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{req.Requirement}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{req.Description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}